import type { FC, ReactNode } from 'react'
import type { AudienceIcon } from '@/constants/content'

import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { PixelStack } from '@/components/pixel/pixel-sprite'

import PixelFlower from '@/components/pixel/pixel-flower'

import { AUDIENCES } from '@/constants/content'
import {
    ICON_CLOUD,
    ICON_EYE_CLOSED,
    ICON_EYE_OPEN,
    ICON_HEART,
    ICON_ROBOT,
    ICON_ROBOT_EYES
} from '@/constants/sprites'

interface AudienceIconArtProps {
    icon: AudienceIcon
    className?: string
}

const ICON_PLACEMENT: Record<AudienceIcon, string> = {
    robot: '-top-14 -left-3 w-[86%] md:-top-16',
    eye: 'top-[24%] -left-6 w-[96%]',
    cloud: '-top-12 left-[10%] w-[82%] md:-top-14',
    heart: '-top-12 -right-5 w-[62%]'
}

const CARD_OFFSETS = ['', 'sm:mt-16 lg:mt-24', 'lg:mt-44', 'sm:mt-16 lg:mt-8']

const AudienceIconArt: FC<AudienceIconArtProps> = ({ icon, className }): ReactNode => {
    if (icon === 'robot') {
        return (
            <PixelStack
                className={cn('animate-hop', className)}
                layers={[{ rows: ICON_ROBOT }, { rows: ICON_ROBOT_EYES, className: 'animate-look' }]}
            />
        )
    }

    if (icon === 'eye') {
        return (
            <PixelStack
                className={className}
                layers={[
                    { rows: ICON_EYE_OPEN, className: 'animate-blink' },
                    { rows: ICON_EYE_CLOSED, className: 'animate-blink-closed opacity-0' }
                ]}
            />
        )
    }

    if (icon === 'cloud') {
        return <PixelStack className={cn('animate-float', className)} layers={[{ rows: ICON_CLOUD }]} />
    }

    return <PixelStack className={cn('animate-heartbeat', className)} layers={[{ rows: ICON_HEART }]} />
}

const AudiencesSection: FC = (): ReactNode => {
    return (
        <section id='services-for' aria-labelledby='audiences-title' className='relative overflow-x-clip pt-32 pb-24 md:pt-40 md:pb-36'>
            <PixelFlower className='pointer-events-none absolute top-24 -left-16 w-48 animate-sway md:left-[4%] md:w-60' />

            <div className='mx-auto max-w-7xl px-5 md:px-8'>
                <h2 id='audiences-title' className='text-center font-heading text-[clamp(3rem,9vw,7.5rem)] leading-[0.85]'>
                    services
                    <br />
                    for
                </h2>

                <p className='mx-auto mt-6 max-w-lg text-center text-base leading-relaxed font-semibold md:text-lg'>
                    Whatever you are building, we speak its language. These are the teams we grow with.
                </p>

                <ul className='mt-24 grid gap-x-6 gap-y-24 sm:grid-cols-2 lg:mt-32 lg:grid-cols-4 lg:gap-x-8'>
                    {AUDIENCES.map((audience, index) => (
                        <Reveal as='li' key={audience.id} delay={index * 130} className={CARD_OFFSETS[index]}>
                            <article className='group'>
                                <div className='relative aspect-[4/5] border-2 border-ink bg-bubblegum shadow-pixel transition-[translate,rotate,box-shadow] duration-200 ease-[steps(3,end)] group-hover:-translate-y-2 group-hover:-rotate-1 group-hover:shadow-pixel-lg'>
                                    <AudienceIconArt
                                        icon={audience.icon}
                                        className={cn('pointer-events-none absolute h-auto', ICON_PLACEMENT[audience.icon])}
                                    />

                                    <h3 className='absolute inset-x-5 bottom-5 text-lg leading-tight font-bold md:text-xl'>
                                        {audience.title}
                                    </h3>
                                </div>

                                <p className='mt-5 max-w-[17rem] text-sm leading-relaxed font-medium md:text-base'>
                                    {audience.text}
                                </p>
                            </article>
                        </Reveal>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export default AudiencesSection
